import { Injectable, inject } from '@angular/core';
import { AuthService } from '@vdms-hq/auth';
import { UIConfirmationDialogService } from '@vdms-hq/ui';
import { TourService } from 'ngx-ui-tour-md-menu';
import { TourStepFilterService } from './tour-guide-settings.service';
import { catchError, concatMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { of } from 'rxjs';

/** Starts the tour from the menu or the hub, and records it finished when the tour ends. */
@Injectable({ providedIn: 'root' })
export class TourGuideService {
  private authService = inject(AuthService);
  private confirmationDialog = inject(UIConfirmationDialogService);
  private tourService = inject(TourService);
  private tourStepFilter = inject(TourStepFilterService);
  private router = inject(Router);

  /** Hub return route. Finish and close share `end$`, which carries no payload. */
  #returnTo: string | null = null;

  // Open the prompt after the write emits. A failed write stays on this stream.
  #tourEnd = this.tourService.end$
    .pipe(
      concatMap(() => {
        const returnTo = this.#returnTo;
        this.#returnTo = null;
        return this.#markAsCompleted().pipe(
          catchError(() => of(undefined)),
          tap(() => {
            if (returnTo) {
              this.#promptOnboardingComplete(returnTo);
            }
          }),
        );
      }),
    )
    .subscribe();

  /** Manually triggers the tour (e.g. from the "Tour guide" menu item). */
  runGuide(): void {
    // Drop a hub route that never reached end$.
    this.#returnTo = null;
    this.#startTour();
  }

  /** Starts the tour from the onboarding card and offers `returnTo` when that tour ends. */
  runGuideFromHub(returnTo: string): void {
    this.#returnTo = returnTo;
    this.#startTour();
  }

  #startTour(): void {
    this.tourStepFilter.filteredSteps$.pipe(take(1)).subscribe((steps) => {
      this.tourService.initialize(steps);
      this.tourService.start();
    });
  }

  #promptOnboardingComplete(returnTo: string): void {
    this.confirmationDialog
      .open({
        title: 'common.onboarding.tour_done.title',
        message: 'common.onboarding.tour_done.message',
        okAction: { label: 'common.onboarding.tour_done.back', color: 'primary' },
        abortAction: { label: 'common.onboarding.tour_done.continue', color: 'secondary' },
      })
      .pipe(take(1))
      .subscribe((back) => {
        if (back) {
          void this.router.navigateByUrl(returnTo);
        }
      });
  }

  #markAsCompleted() {
    return this.authService.updateAttributes({
      vida: {
        finishedTour: true,
      },
    });
  }
}
